import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Loader2, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }));

export default function SubappaltoDocument() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [archived, setArchived] = useState(false);

  useEffect(() => {
    document.title = "Contratto di subappalto";
    api.get(`/leads/${id}/subappalto`)
      .then((r) => { setDoc(r.data); setArchived(!!r.data?.pdf_archived); })
      .catch((e) => setErr(e.response?.data?.detail || "Documento non disponibile"))
      .finally(() => setLoading(false));
  }, [id]);

  const buildPdf = async () => {
    const el = document.getElementById("subappalto-print");
    if (!el) return null;
    const opt = {
      margin: [10, 10, 12, 10],
      image: { type: "jpeg", quality: 0.96 },
      html2canvas: { scale: 1.6, useCORS: true, backgroundColor: "#ffffff", windowWidth: 1120 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };
    return await html2pdf().set(opt).from(el).toPdf().get("pdf");
  };

  const filenameFor = () => {
    const name = (doc?.subappaltatore?.ragione_sociale || "installatore").replace(/[^A-Za-z0-9]+/g, "_");
    return `Subappalto_${doc?.contratto_principale_n || ""}_${name}.pdf`;
  };

  useEffect(() => {
    if (!doc || archived) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const pdf = await buildPdf();
        if (!pdf || cancelled) return;
        const dataUri = pdf.output("datauristring");
        const pdf_base64 = dataUri.split(",", 2)[1] || dataUri;
        await api.post(`/leads/${id}/subappalto-pdf`, { pdf_base64, filename: filenameFor() });
        if (!cancelled) setArchived(true);
      } catch (e) { /* silenzioso */ }
    }, 900);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, archived, id]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try { const pdf = await buildPdf(); pdf.save(filenameFor()); }
    catch (e) { toast.error("Errore nella generazione del PDF. Riprova."); }
    finally { setPdfLoading(false); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-[#C99700]" /></div>;
  if (err || !doc) return <div className="flex h-screen items-center justify-center bg-white text-neutral-700 px-6 text-center">{err || "Documento non disponibile."}</div>;

  const s = doc.subappaltatore || {};
  const sedeSub = [s.indirizzo, [s.cap, s.comune].filter(Boolean).join(" "), s.provincia ? `(${s.provincia})` : ""].filter(Boolean).join(", ");

  return (
    <div className="print-surface min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2">
          <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-9 w-9 rounded-full object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-sm tracking-wide text-neutral-900">TORRI.SOLAR</div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-[0.15em]">Contratto di subappalto</div>
          </div>
        </div>
        <Button onClick={downloadPdf} disabled={pdfLoading} className="font-semibold bg-[#FDD003] text-black hover:bg-[#e6d600]" data-testid="download-subappalto-pdf-btn">
          {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          {pdfLoading ? "Genero PDF…" : "Scarica PDF"}
        </Button>
      </div>

      <div id="subappalto-print" className="mx-auto max-w-3xl px-6 sm:px-8 py-6 text-[11px] leading-snug" style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#171717" }}>
        <div className="text-center border-b border-neutral-300 pb-2 mb-3">
          <h1 className="text-base font-extrabold uppercase tracking-wide">Contratto di subappalto per la realizzazione di impianti fotovoltaici</h1>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-0.5">Appaltatore</div>
            {(doc.appaltatore_linee || []).map((l, i) => <div key={i} className={i === 0 ? "font-semibold" : "text-[10px] text-neutral-600"}>{l}</div>)}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-0.5">Subappaltatore</div>
            <div className="font-semibold">{s.ragione_sociale || "—"}</div>
            {s.partita_iva && <div className="text-[10px] text-neutral-600">P.IVA/C.F.: {s.partita_iva}</div>}
            {sedeSub && <div className="text-[10px] text-neutral-600">{sedeSub}</div>}
            {s.telefono && <div className="text-[10px] text-neutral-600">Tel: {s.telefono}</div>}
            {s.email && <div className="text-[10px] text-neutral-600">{s.email}</div>}
          </div>
        </div>

        {(doc.articoli || []).map((a, i) => {
          if (a.tipo === "materiali") {
            const hasVal = (doc.distinta || []).some((d) => d.valore != null && d.valore !== "");
            return (
              <div key={i} className="mb-3 break-inside-avoid">
                <div className="font-bold text-[11px] mb-1">{a.titolo}</div>
                {a.testo && <p className="text-[10.5px] text-neutral-700 mb-1.5" style={{ whiteSpace: "pre-wrap" }}>{a.testo}</p>}
                <table className="w-full border-collapse text-[10.5px]">
                  <thead><tr className="border-b border-neutral-400 text-left">
                    <th className="py-0.5 pr-2 font-semibold w-14">Q.tà</th>
                    <th className="py-0.5 pr-2 font-semibold">Codice</th>
                    <th className="py-0.5 font-semibold">Descrizione</th>
                    {hasVal && <th className="py-0.5 pl-2 font-semibold text-right w-24">Valore</th>}
                  </tr></thead>
                  <tbody>
                    {(doc.distinta || []).map((d, j) => (
                      <tr key={j} className="border-b border-neutral-100 align-top">
                        <td className="py-0.5 pr-2 font-mono">{d.qta}</td>
                        <td className="py-0.5 pr-2 font-medium">{d.codice}</td>
                        <td className="py-0.5 text-neutral-700">{d.descrizione}</td>
                        {hasVal && <td className="py-0.5 pl-2 text-right font-mono whitespace-nowrap">{d.valore != null && d.valore !== "" ? eur(d.valore) : "\u2014"}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-neutral-600 mt-1.5">Il Subappaltatore è responsabile della corretta installazione, della custodia dei materiali in cantiere e della segnalazione tempestiva di eventuali difetti o mancanze.</p>
              </div>
            );
          }
          if (a.tipo === "corrispettivo") {
            return (
              <div key={i} className="mb-2.5 break-inside-avoid">
                <span className="font-bold text-[11px]">{a.titolo}. </span>
                <span className="text-[10.5px] text-neutral-700">A corpo: </span>
                <span className="font-bold text-[11px]" data-testid="subappalto-corrispettivo">{eur(doc.corrispettivo)}</span>
                {a.testo && <span className="text-[10.5px] text-neutral-700" style={{ whiteSpace: "pre-wrap" }}>{" — " + a.testo}</span>}
              </div>
            );
          }
          return (
            <div key={i} className="mb-2.5 break-inside-avoid">
              <span className="font-bold text-[11px]">{a.titolo}. </span>
              <span className="text-[10.5px] text-neutral-700" style={{ whiteSpace: "pre-wrap" }}>{a.testo}</span>
            </div>
          );
        })}

        <p className="text-[10.5px] text-neutral-700 mt-4 mb-2">Letto, confermato e sottoscritto.</p>
        <div className="text-[10.5px] mb-6">Data: _______________________________</div>
        <div className="grid grid-cols-2 gap-8 mt-8">
          <div className="border-t border-neutral-500 pt-1 text-center text-[10px]">
            <div className="font-semibold">L'Appaltatore</div><div className="text-neutral-600">Fier S.p.A.</div>
          </div>
          <div className="border-t border-neutral-500 pt-1 text-center text-[10px]">
            <div className="font-semibold">Il Subappaltatore</div><div className="text-neutral-600">{s.ragione_sociale || ""}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
